import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Edit2, Save, X, Sparkles, Send, FileText, Archive, ArchiveRestore, Trash2, AlertCircle, CheckCircle, Users, Paperclip, Download } from 'lucide-react';
import Header from './Header';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
import TrustCenterInclusionCard from './TrustCenterInclusionCard';
import {
  VersionSelectorButton,
  SourceBadge,
  UploadPdfVersionDialog,
  AIRegenerateVersionDialog,
  HistoricalVersionBanner,
} from './PolicyVersioning';

// Using relative URLs

// Remember which version the user was last viewing for a given policy, so
// reopening the policy returns to that version instead of always snapping
// back to the active one. Scoped per policy id in localStorage.
const versionStorageKey = (pid) => `nova.policyPreviewVersion.${pid}`;
const readRememberedVersion = (pid) => {
  try {
    const n = parseInt(localStorage.getItem(versionStorageKey(pid)), 10);
    return Number.isFinite(n) && n >= 1 ? n : null;
  } catch { return null; }
};
const rememberVersion = (pid, n) => {
  try { if (pid && n) localStorage.setItem(versionStorageKey(pid), String(n)); } catch { /* ignore */ }
};

const PolicyDetail = React.forwardRef(({ policyId: policyIdProp, onClose, isModal: isModalProp } = {}, ref) => {
  const params = useParams();
  const policyId = policyIdProp || params.policyId;
  const isModal = isModalProp ?? Boolean(policyIdProp && onClose);
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    company_name: '',
    audience: '',
    must_include: '',
    must_avoid: '',
    additional_context: '',
  });
  const [acceptances, setAcceptances] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: ''
  });

  // Dialog states
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showUnpublishDialog, setShowUnpublishDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showUnarchiveDialog, setShowUnarchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAcceptancesDialog, setShowAcceptancesDialog] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');

  // ---- Versioning state -------------------------------------------------
  // The Policy detail always renders whichever version is currently "in
  // preview". By default that is the active version (whatever the backend
  // says is is_active=true). Selecting a historical version updates the
  // preview WITHOUT touching activation — activation requires an explicit
  const [previewVersionNumber, setPreviewVersionNumber] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(null); // full snapshot
  const [activatingVersion, setActivatingVersion] = useState(false);
  const [showUploadPdfDialog, setShowUploadPdfDialog] = useState(false);
  const [showAIRegenDialog, setShowAIRegenDialog] = useState(false);

  // ---- Attachments state ------------------------------------------------
  // Attachments live SEPARATELY from policy content / versions — they are
  // a many-to-many link between a policy and a reusable uploaded file.
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [removingLinkId, setRemovingLinkId] = useState(null);
  // PDF preview dialog state: { fileId, filename, url, error, loading }
  const [pdfPreview, setPdfPreview] = useState(null);
  // Typed-confirmation state for hard-deleting an uploaded PDF and the
  // policy itself. Both keep the destructive action gated on the user
  // typing DELETE exactly.
  const [pendingRemoveFile, setPendingRemoveFile] = useState(null);
  const [policyDeleteTyped, setPolicyDeleteTyped] = useState('');

  // Unsaved-changes guard: { open, pendingAction } — pendingAction is invoked
  // when the user picks Discard; Save runs handleUpdate then runs pendingAction.
  const [unsavedPrompt, setUnsavedPrompt] = useState({ open: false, pendingAction: null });

  // Snapshot of the last persisted policy so we can compute `dirty` without
  // round-tripping through the server. Inlined name resolution avoids a TDZ
  // with the `getPolicyName` helper declared further down.
  const snapshot = React.useMemo(() => ({
    name: policy ? (policy.name || policy.title || 'Unknown') : '',
    description: policy?.description || '',
    content: policy?.content || '',
  }), [policy]);
  const dirty = editing && (
    (formData.name || '') !== snapshot.name ||
    (formData.description || '') !== snapshot.description ||
    (formData.content || '') !== snapshot.content
  );

  // Browser-level safety net: warn before unload if there are unsaved edits.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirty) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // Expose an imperative `requestExit(onContinue)` to the parent modal.
  // The parent calls this whenever the user attempts to close (X icon,
  // overlay click, Esc). If the editor is dirty we pop the save/discard
  // prompt; if not, we run `onContinue()` immediately. We track the latest
  // `dirty` flag in a ref because Radix's onOpenChange fires once and we
  // want the freshest value without recreating the handle every render.
  const dirtyRef = React.useRef(dirty);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  React.useImperativeHandle(ref, () => ({
    requestExit(onContinue) {
      if (dirtyRef.current) {
        setUnsavedPrompt({ open: true, pendingAction: onContinue || null });
        return false;
      }
      if (typeof onContinue === 'function') onContinue();
      return true;
    },
    isDirty: () => dirtyRef.current,
  }), []);

  useEffect(() => {
    fetchPolicy();
    fetchAcceptances();
    fetchAttachments();
  }, [policyId]);

  // Re-scope attachments whenever the user picks a different version
  // from the version selector. Server-side filter narrows to that
  // version PLUS legacy (version-agnostic) links, so bulk-attach items
  // remain visible on every version.
  useEffect(() => {
    if (!policyId) return;
    if (!previewVersionNumber) return;
    fetchAttachments(previewVersionNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewVersionNumber, policyId]);

  const fetchAttachments = async (versionNumber = null) => {
    if (!policyId) return;
    setAttachmentsLoading(true);
    try {
      const url = versionNumber
        ? `/api/policies/${policyId}/attachments?version_number=${encodeURIComponent(versionNumber)}`
        : `/api/policies/${policyId}/attachments`;
      const r = await axios.get(url, getAuthHeaders());
      setAttachments(r.data || []);
    } catch (e) {
      // Non-fatal — attachments UI just shows empty state.
      setAttachments([]);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleAttachmentOpen = async (fileId, filename) => {
    /* PDF preview: open in an in-app Dialog rather than a new tab.
       Downloads the file into a blob URL and hands it to
       PolicyPdfPreviewDialog for inline rendering. Any error surfaces
       a toast + fallback download prompt inside the dialog. */
    if (!fileId) return;
    setPdfPreview({ fileId, filename: filename || 'document.pdf', url: null, error: null, loading: true });
    try {
      const res = await axios.get(
        `/api/policy-attachments/${fileId}/download`,
        { ...getAuthHeaders(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: res.data.type || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfPreview((p) => (p && p.fileId === fileId ? { ...p, url, loading: false } : p));
    } catch (err) {
      setPdfPreview((p) => (p && p.fileId === fileId
        ? { ...p, error: 'Unable to load PDF preview.', loading: false }
        : p));
    }
  };

  const closePdfPreview = () => {
    setPdfPreview((prev) => {
      if (prev?.url) {
        try { URL.revokeObjectURL(prev.url); } catch { /* noop */ }
      }
      return null;
    });
  };

  const handleAttachmentDownload = async (fileId, filename) => {
    if (!fileId) return;
    try {
      const res = await axios.get(`/api/policy-attachments/${fileId}/download`, { ...getAuthHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'attachment.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30 * 1000);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const handleAttachmentRemove = async (linkId, filename) => {
    /* Opens the typed-DELETE confirmation dialog. The actual DELETE
       call is fired inside `confirmAttachmentRemove` only after the
       user types DELETE and presses "Permanently Remove PDF". */
    if (!linkId) return;
    setPendingRemoveFile({ linkId, filename: filename || 'this file', typed: '', submitting: false, error: '' });
  };

  const confirmAttachmentRemove = async () => {
    const p = pendingRemoveFile;
    if (!p || p.typed !== 'DELETE' || p.submitting) return;
    setPendingRemoveFile({ ...p, submitting: true, error: '' });
    try {
      await axios.delete(`/api/policies/${policyId}/attachments/${p.linkId}`, getAuthHeaders());
      toast.success('Attachment removed');
      await fetchAttachments(previewVersionNumber || undefined);
      setPendingRemoveFile(null);
    } catch (e) {
      setPendingRemoveFile((prev) => (prev
        ? { ...prev, submitting: false, error: 'Could not remove the file. Please try again.' }
        : prev));
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const getPolicyName = (policy) => {
    // Fallback logic: try name first, then title, then 'Unknown'
    return policy?.name || policy?.title || 'Unknown';
  };

  const fetchPolicy = async () => {
    try {
      const response = await axios.get(`/api/policies/${policyId}`, getAuthHeaders());
      setPolicy(response.data);
      setFormData({
        name: response.data.name,
        description: response.data.description || '',
        content: response.data.content || ''
      });
      // Default the preview to whichever version is currently active…
      const activeNum = response.data.active_version_number || response.data.version || 1;
      const versionsList = response.data.versions || [];
      const activeSnapshot = {
        version_number: activeNum,
        name: response.data.name,
        description: response.data.description || '',
        content: response.data.content || '',
        source_type: versionsList.find((v) => v.version_number === activeNum)?.source_type || 'original',
        is_active: true,
      };
      // …but if the user last viewed a different (still-existing) version for
      // this policy, reopen to THAT version instead of snapping back to active.
      const remembered = readRememberedVersion(policyId);
      const targetNum = (remembered && remembered !== activeNum
        && versionsList.some((v) => v.version_number === remembered))
        ? remembered
        : activeNum;
      setPreviewVersionNumber(targetNum);
      if (targetNum === activeNum) {
        // The active version's snapshot lives on the parent policy already,
        // so we don't need a second round-trip when previewing the active.
        setPreviewVersion(activeSnapshot);
      } else {
        try {
          const vr = await axios.get(
            `/api/policies/${policyId}/versions/${targetNum}`,
            getAuthHeaders(),
          );
          setPreviewVersion(vr.data);
        } catch {
          // Version fetch failed — fall back to the active version cleanly.
          setPreviewVersionNumber(activeNum);
          setPreviewVersion(activeSnapshot);
        }
      }
    } catch (error) {
      toast.error('Failed to load policy');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Load a specific version snapshot into the preview panel (does NOT
  // activate it). Called from the version-selector popover.
  const handleSelectPreviewVersion = async (versionNumber) => {
    if (!policy || !versionNumber) return;
    const activeNum = policy.active_version_number || policy.version || 1;
    setPreviewVersionNumber(versionNumber);
    rememberVersion(policyId, versionNumber);  // remember for next reopen
    if (versionNumber === activeNum) {
      setPreviewVersion({
        version_number: activeNum,
        name: policy.name,
        description: policy.description || '',
        content: policy.content || '',
        source_type: (policy.versions || []).find((v) => v.version_number === activeNum)?.source_type || 'original',
        is_active: true,
      });
      return;
    }
    try {
      const r = await axios.get(
        `/api/policies/${policyId}/versions/${versionNumber}`,
        getAuthHeaders(),
      );
      setPreviewVersion(r.data);
    } catch (e) {
      toast.error('Failed to load version');
    }
  };

  // Roll back / promote: make the version currently being previewed the
  // ACTIVE (default) version going forward. Uses the existing activate
  // endpoint, then refreshes so the banner clears and badges update.
  const handleActivatePreviewVersion = async () => {
    if (!previewVersionNumber || activatingVersion) return;
    setActivatingVersion(true);
    try {
      await axios.post(
        `/api/policies/${policyId}/versions/${previewVersionNumber}/activate`,
        {},
        getAuthHeaders(),
      );
      rememberVersion(policyId, previewVersionNumber);
      toast.success(`Version ${previewVersionNumber} is now the active version.`);
      await fetchPolicy();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to set active version');
    } finally {
      setActivatingVersion(false);
    }
  };

  // Exit preview → return to the active version (and remember that choice).
  const handleExitPreview = () => {
    if (!policy) return;
    const activeNum = policy.active_version_number || policy.version || 1;
    handleSelectPreviewVersion(activeNum);
  };

  const fetchAcceptances = async () => {
    try {
      const response = await axios.get(
        `/api/policies/${policyId}/acceptances`,
        getAuthHeaders()
      );
      setAcceptances(response.data);
    } catch (error) {
      console.log('Could not fetch acceptances:', error);
    }
  };

  const handleUpdate = async (override) => {
    const payload = override || formData;
    try {
      await axios.put(
        `/api/policies/${policyId}`,
        payload,
        getAuthHeaders()
      );
      toast.success('Policy updated successfully!');
      setEditing(false);
      await fetchPolicy();
      return true;
    } catch (error) {
      toast.error('Failed to update policy');
      console.error(error);
      return false;
    }
  };

  const openGenerateDialog = () => {
    const policyName = getPolicyName(policy);
    if (!policyName || policyName === 'Unknown') {
      toast.error('Policy must have a name before generating content');
      return;
    }
    // Prefill company name from the signed-in user — they can still
    // override it for this one generation if they want.
    let userCompany = '';
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      userCompany = u?.company_name || '';
    } catch { /* no-op */ }
    setGenerateForm({
      company_name: userCompany,
      audience: '',
      must_include: '',
      must_avoid: '',
      additional_context: '',
    });
    setShowGenerateDialog(true);
  };

  const runAiGenerate = async (e) => {
    e?.preventDefault?.();
    const policyName = getPolicyName(policy);
    if (!policyName || policyName === 'Unknown') {
      toast.error('Policy must have a name before generating content');
      return;
    }
    const companyName = (generateForm.company_name || '').trim();
    if (!companyName) {
      toast.error('Company name is required. Set it on your Profile or fill it here.');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `/api/policies/generate-content`,
        {
          policy_name: policyName,
          frameworks: policy.frameworks,
          description: policy.description,
          company_name: companyName,
          audience: generateForm.audience.trim() || null,
          must_include: generateForm.must_include.trim() || null,
          must_avoid: generateForm.must_avoid.trim() || null,
          additional_context: generateForm.additional_context.trim() || null,
        },
        getAuthHeaders()
      );

      const newContent = response.data.content;
      const nextFormData = { ...formData, content: newContent };
      setFormData(nextFormData);
      setShowGenerateDialog(false);

      // Auto-save the AI-generated content so the user doesn't lose it if
      // they forget to click Save. We re-enter editing mode immediately so
      // they can still tweak the content right after.
      try {
        await axios.put(
          `/api/policies/${policyId}`,
          nextFormData,
          getAuthHeaders()
        );
        await fetchPolicy();
        setEditing(true);
        toast.success(`Generated and saved for ${response.data.company_name || companyName}`);
      } catch (saveErr) {
        // Saving failed but content is in the editor — let the user retry manually.
        console.error('auto-save after AI generation failed', saveErr);
        toast.error('Generated, but auto-save failed. Click Save to keep the changes.');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Failed to generate content';
      toast.error(detail);
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (!formData.content || formData.content.trim().length === 0) {
      toast.error('Please add policy content before publishing');
      return;
    }

    try {
      await axios.post(
        `/api/policies/${policyId}/publish`,
        {},
        getAuthHeaders()
      );
      toast.success('Policy published! Employees will be notified.');
      setShowPublishDialog(false);
      fetchPolicy();
      fetchAcceptances();
    } catch (error) {
      toast.error('Failed to publish policy');
      console.error(error);
    }
  };

  const handleUnpublish = async () => {
    if (confirmationText !== 'UNPUBLISH') {
      toast.error('Please type UNPUBLISH to confirm');
      return;
    }

    try {
      await axios.post(
        `/api/policies/${policyId}/unpublish`,
        {},
        getAuthHeaders()
      );
      toast.success('Policy unpublished. You can now edit and re-publish.');
      setShowUnpublishDialog(false);
      setConfirmationText('');
      fetchPolicy();
      fetchAcceptances();
    } catch (error) {
      toast.error('Failed to unpublish policy');
      console.error(error);
    }
  };

  const handleArchive = async () => {
    try {
      await axios.post(
        `/api/policies/${policyId}/archive`,
        {},
        getAuthHeaders()
      );
      toast.success('Policy archived successfully');
      setShowArchiveDialog(false);
      fetchPolicy();
    } catch (error) {
      toast.error('Failed to archive policy');
      console.error(error);
    }
  };

  const handleUnarchive = async () => {
    try {
      await axios.post(
        `/api/policies/${policyId}/unarchive`,
        {},
        getAuthHeaders()
      );
      toast.success('Policy unarchived successfully');
      setShowUnarchiveDialog(false);
      fetchPolicy();
    } catch (error) {
      toast.error('Failed to unarchive policy');
      console.error(error);
    }
  };

  const handleDelete = async () => {
    if (confirmationText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      await axios.delete(
        `/api/policies/${policyId}`,
        getAuthHeaders()
      );
      toast.success('Policy deleted permanently');
      setShowDeleteDialog(false);
      setConfirmationText('');
      if (isModal && onClose) {
        onClose({ deleted: true, policyId });
      } else {
        navigate('/policies');
      }
    } catch (error) {
      toast.error('Failed to delete policy');
      console.error(error);
    }
  };

  const isAllAccepted = () => {
    if (!acceptances || !acceptances.total_employees) return false;
    return acceptances.accepted_count === acceptances.total_employees && acceptances.total_employees > 0;
  };

  if (loading) {
    return (
      <div className={isModal ? "bg-white" : "min-h-screen bg-appbg"}>
        {!isModal && <Header />}
        <div className={`flex items-center justify-center ${isModal ? 'h-96' : 'min-h-screen'}`}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className={isModal ? "bg-white" : "min-h-screen bg-appbg"}>
        {!isModal && <Header />}
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Policy Not Found</h2>
          <Button onClick={() => (isModal && onClose ? onClose() : navigate('/policies'))}>
            {isModal ? 'Close' : 'Back to Policies'}
          </Button>
        </div>
      </div>
    );
  }

  // Back / close handler — routed through the dirty guard so accidental
  // navigation away never throws away unsaved edits.
  const handleBackOrClose = () => {
    const doExit = () => (isModal && onClose ? onClose() : navigate('/policies'));
    if (dirty) {
      setUnsavedPrompt({ open: true, pendingAction: doExit });
    } else {
      doExit();
    }
  };

  // Action buttons rendered in the sticky toolbar. View-mode shows lifecycle
  // actions (Edit / Publish / Archive / Delete …); edit-mode collapses to
  // Cancel + Save so the primary action stays one click away on long
  // policies.
  const renderActionButtons = () => {
    if (!editing) {
      // Versioning quick actions — icon-only buttons that sit immediately
      // to the LEFT of Edit. Disabled while previewing a historical
      // version (they always operate on the currently-viewed snapshot,
      // so we want the user to explicitly exit preview or activate
      // first — a subtle "no-op if you didn't mean it" guard).
      const canEdit = policy.status !== 'published' && policy.status !== 'archived';
      return (
        <>
          <Button
            variant="outline"
            size="sm"
            aria-label="Upload your own PDF"
            title="Upload your own PDF"
            onClick={() => setShowUploadPdfDialog(true)}
            disabled={!canEdit}
            data-testid="policy-upload-pdf-btn"
            className="px-2"
          >
            <Paperclip className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-label="Regenerate with AI"
            title="Regenerate with AI"
            onClick={() => setShowAIRegenDialog(true)}
            disabled={!canEdit}
            data-testid="policy-ai-regen-btn"
            className="px-2"
          >
            <Sparkles className="w-4 h-4 text-violet-600" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
            disabled={policy.status === 'published' || policy.status === 'archived'}
            data-testid="policy-edit-btn"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            Edit
          </Button>
          {policy.status === 'draft' && (
            /* Publish eligibility depends purely on lifecycle status +
               permissions — NOT on whether content is text or an
               uploaded PDF. Previously required non-empty
               `policy.content`, which hid Publish after a PDF upload
               (since PDF-source versions intentionally carry empty
               `content`). See tests: `test_publish_visible_for_pdf_upload_draft`. */
            <Button
              size="sm"
              onClick={() => setShowPublishDialog(true)}
              className="bg-navy-600 hover:bg-navy-700"
              data-testid="policy-publish-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Publish
            </Button>
          )}
          {policy.status === 'published' && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowUnpublishDialog(true)}
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                data-testid="policy-unpublish-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Unpublish
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAcceptancesDialog(true)}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="policy-acceptances-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                View Acceptances
              </Button>
            </>
          )}
          {(policy.status === 'draft' || policy.status === 'published') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowArchiveDialog(true)}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="policy-archive-btn"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archive
            </Button>
          )}
          {policy.status === 'archived' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowUnarchiveDialog(true)}
              className="border-emerald-300 text-navy-700 hover:bg-emerald-50"
              data-testid="policy-unarchive-btn"
            >
              <ArchiveRestore className="w-4 h-4 mr-2" />
              Unarchive
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeleteDialog(true)}
            className="border-red-300 text-red-700 hover:bg-red-50"
            data-testid="policy-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
          {isModal && (
            /* Modal close: icon-only, aria-labelled, pinned to the far
               right of the toolbar. Height/padding matched to the
               size="sm" action buttons (h-9) so the X sits inline with
               Edit / Publish / Archive / Delete. */
            <Button
              variant="outline"
              size="sm"
              onClick={handleBackOrClose}
              aria-label="Close policy details"
              title="Close"
              data-testid="policy-close-icon-btn"
              className="ml-1 h-9 w-9 p-0 flex items-center justify-center shrink-0"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </>
      );
    }
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          data-testid="policy-edit-cancel"
          onClick={() => {
            const doDiscard = () => {
              setEditing(false);
              setFormData({
                name: getPolicyName(policy),
                description: policy.description || '',
                content: policy.content || ''
              });
            };
            if (dirty) {
              setUnsavedPrompt({ open: true, pendingAction: doDiscard });
            } else {
              doDiscard();
            }
          }}
        >
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => handleUpdate()}
          data-testid="policy-edit-save"
          className={dirty ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
        >
          <Save className="w-4 h-4 mr-2" />
          {dirty ? 'Save changes' : 'Save'}
        </Button>
      </>
    );
  };

  return (
    <div className={isModal ? "bg-white" : "min-h-screen bg-appbg"}>
      {!isModal && <Header />}

      {/* Sticky Action Toolbar — pins to the top of the scroll container so
          Save / Edit / Publish are always one click away, even on long
          policies. Sits OUTSIDE the title card so its sticky context is the
          modal/page scroll area, not the (short) header card. */}
      <div
        className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm"
        data-testid="policy-action-toolbar"
      >
        <div className={isModal ? "px-4 sm:px-6 lg:px-8 py-3" : "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3"}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              {!isModal && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBackOrClose}
                  data-testid="policy-detail-back"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
              {!editing && (
                <div className="hidden sm:flex items-center gap-2 min-w-0">
                  <span className="text-sm font-semibold text-slate-900 truncate max-w-[280px]" title={getPolicyName(policy)}>
                    {getPolicyName(policy)}
                  </span>
                  <Badge
                    variant={policy.status === 'published' ? 'default' : 'secondary'}
                    className="text-[10px] uppercase tracking-wide"
                  >
                    {policy.status}
                  </Badge>
                  {dirty && (
                    <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50">
                      Unsaved
                    </Badge>
                  )}
                </div>
              )}
              {editing && dirty && (
                <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50">
                  Unsaved changes
                </Badge>
              )}
            </div>
            <div className="flex gap-2 flex-wrap" data-testid="policy-action-buttons">
              {renderActionButtons()}
            </div>
          </div>
        </div>
      </div>

      <div className={isModal ? "px-4 sm:px-6 lg:px-8 py-6" : "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        {/* Header Card — title + description only. Lifecycle actions live
            in the sticky toolbar above so they remain visible while
            scrolling. */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex-1">
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <Label>Policy Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="text-lg font-bold"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                    />
                  </div>
                </div>
              ) : (
                <>
                  {/* In view-mode, mirror the currently PREVIEWED version's
                      title/description. The active version's snapshot equals
                      policy.name/policy.description so this is a no-op for
                      the default case, but switching to a historical version
                      updates the header without changing anything on disk. */}
                  <CardTitle className="text-3xl text-slate-900 mb-2" data-testid="policy-title-preview">
                    {previewVersion?.name || getPolicyName(policy)}
                  </CardTitle>
                  {(previewVersion?.description || policy.description) && (
                    <CardDescription className="text-base" data-testid="policy-description-preview">
                      {previewVersion?.description || policy.description}
                    </CardDescription>
                  )}
                </>
              )}
            </div>
          </CardHeader>

          {/* Acceptance Stats Banner */}
          {policy.acceptance_stats && policy.status === 'published' && (
            <div className="px-6 py-4 bg-gradient-to-r from-navy-50 to-white border-y border-emerald-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white rounded-lg p-2">
                    <Users className="w-6 h-6 text-navy-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 font-medium">Acceptance Status</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {policy.acceptance_stats.accepted_count} of {policy.acceptance_stats.total_employees} accepted
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-emerald-200">
                    <CheckCircle className="w-5 h-5 text-navy-600" />
                    <span className="text-xl font-bold text-navy-600">
                      {Math.round(policy.acceptance_stats.acceptance_rate)}%
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Compliance Rate</p>
                </div>
              </div>
            </div>
          )}
          
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              {policy.frameworks && policy.frameworks.map((fw) => (
                <Badge key={fw} variant="outline">{fw}</Badge>
              ))}
              <Badge variant={policy.status === 'published' ? 'default' : 'secondary'}>
                {policy.status}
              </Badge>
              <VersionSelectorButton
                versions={policy.versions || []}
                activeVersionNumber={policy.active_version_number || policy.version || 1}
                previewVersionNumber={previewVersionNumber}
                onSelect={handleSelectPreviewVersion}
              />
              <SourceBadge sourceType={previewVersion?.source_type} />
            </div>
            {/* Historical-version banner: shown while previewing a version
                that is NOT the active one. Lets the user roll back / promote
                the previewed version to be the active default going forward. */}
            {previewVersionNumber
              && previewVersionNumber !== (policy.active_version_number || policy.version || 1) && (
              <HistoricalVersionBanner
                previewVersionNumber={previewVersionNumber}
                onActivate={handleActivatePreviewVersion}
                onExitPreview={handleExitPreview}
                activating={activatingVersion}
                disabled={policy.status === 'published'}
                disabledReason={policy.status === 'published'
                  ? 'Unpublish this policy first to change which version is active.'
                  : ''}
              />
            )}
            {policy.status === 'published' && !isAllAccepted() && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  ℹ️ This policy is currently live. To make changes, click "Unpublish" first, then you can edit and re-publish.
                </p>
              </div>
            )}
            {policy.status === 'published' && isAllAccepted() && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-navy-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800 mb-1">
                      All Employees Accepted! 🎉
                    </p>
                    <p className="text-sm text-navy-700">
                      All {acceptances?.total_employees} employees have accepted this policy.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {policy.status === 'archived' && (
              <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-sm text-slate-700">
                  📦 This policy is archived. Click "Unarchive" to restore it to draft status.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trust Center inclusion — quick opt-in/out. Backed by the
            existing PATCH /api/trust-center/policies/{id} endpoint so
            state stays in one place (the trust_centers doc). Detailed
            access, ordering, and description overrides remain in the
            Trust Center Admin — this card is a shortcut only. */}
        <TrustCenterInclusionCard type="policy" id={policyId} name={policy.name} />

        {/* Policy Content Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Policy Content</CardTitle>
              {editing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openGenerateDialog}
                  disabled={generating}
                  data-testid="policy-ai-generate-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {generating ? 'Generating...' : 'AI Generate'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {editing ? (
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={20}
                placeholder="Enter policy content or use AI Generate..."
              />
            ) : (
              <div className="prose max-w-none">
                {(() => {
                  // In view-mode we show the currently PREVIEWED version's
                  // content. For the active version this equals
                  // policy.content; for a historical version it's the
                  // snapshot we fetched via GET /versions/{n}.
                  const previewedContent = (previewVersion?.content ?? policy.content) || '';
                  const previewedSource = previewVersion?.source_type;

                  // Uploaded-PDF versions: render the file(s) INLINE
                  // here instead of pointing users to a separate
                  // Attachments card. Non-PDF (AI-generated) versions
                  // continue to render their text content as before.
                  if (previewedSource === 'pdf_upload') {
                    if (attachmentsLoading) {
                      return <p className="text-sm text-slate-500 italic">Loading document…</p>;
                    }
                    if (attachments.length === 0) {
                      return (
                        <p className="text-sm text-slate-500 italic" data-testid="policy-content-empty-upload">
                          {previewVersionNumber
                            ? `No document uploaded for Version ${previewVersionNumber}.`
                            : 'No document uploaded for this version.'}
                        </p>
                      );
                    }
                    return (
                      <ul className="space-y-2 not-prose" data-testid="policy-content-file-list">
                        {attachments.map((a) => (
                          <AttachedFileRow
                            key={a.link_id}
                            file={a}
                            onOpen={() => handleAttachmentOpen(a.file_id, a.filename)}
                            onDownload={() => handleAttachmentDownload(a.file_id, a.filename)}
                            onRemove={() => handleAttachmentRemove(a.link_id, a.filename)}
                            removing={removingLinkId === a.link_id}
                          />
                        ))}
                      </ul>
                    );
                  }

                  if (previewedContent) {
                    return (
                      <pre className="whitespace-pre-wrap font-sans text-slate-700" data-testid="policy-content-preview">{previewedContent}</pre>
                    );
                  }
                  // Empty content, non-PDF version — still-generating AI text.
                  return (
                    <div className="flex items-center gap-2 text-slate-500 italic">
                      <div className="animate-pulse h-2 w-2 rounded-full bg-blue-400"></div>
                      <p>Policy content is being generated by AI. Refresh the page in a moment.</p>
                    </div>
                  );
                })()}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Metadata Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Metadata</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Created</p>
                <p className="font-medium">{formatDateTime(policy.created_at)}</p>
              </div>
              {policy.published_at && (
                <div>
                  <p className="text-slate-500">Published</p>
                  <p className="font-medium">{formatDateTime(policy.published_at)}</p>
                </div>
              )}
              {policy.archived_at && (
                <div>
                  <p className="text-slate-500">Archived</p>
                  <p className="font-medium">{formatDateTime(policy.archived_at)}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Acceptance Stats - shown for published policies */}
        {policy.status === 'published' && acceptances && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">Employee Acceptance Status</CardTitle>
              <CardDescription>
                {acceptances.accepted_count} of {acceptances.total_employees} employees have accepted this policy
                ({acceptances.acceptance_rate.toFixed(1)}%)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div 
                    className="bg-navy-600 h-3 rounded-full transition-all"
                    style={{width: `${acceptances.acceptance_rate}%`}}
                  ></div>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowAcceptancesDialog(true)}
                className="w-full"
              >
                <Users className="w-4 h-4 mr-2" />
                View Detailed Acceptance Report
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Unsaved-changes confirmation — appears when navigating away while dirty */}
      <Dialog open={unsavedPrompt.open}
              onOpenChange={(open) => { if (!open) setUnsavedPrompt({ open: false, pendingAction: null }); }}>
        <DialogContent className="max-w-md p-0" data-testid="unsaved-changes-dialog">
          <DialogHeader className="px-5 pt-4 pb-3 border-b border-slate-100">
            <DialogTitle className="text-base flex items-center gap-2 text-slate-900">
              <Save className="w-4 h-4 text-emerald-600" />
              Save your changes?
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              You have unsaved edits to this policy. What would you like to do?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="px-5 py-3 border-t border-slate-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setUnsavedPrompt({ open: false, pendingAction: null })}
              data-testid="unsaved-keep-editing"
            >
              Keep editing
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-50"
              onClick={() => {
                const fn = unsavedPrompt.pendingAction;
                setUnsavedPrompt({ open: false, pendingAction: null });
                if (typeof fn === 'function') fn();
              }}
              data-testid="unsaved-discard"
            >
              Discard
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={async () => {
                const fn = unsavedPrompt.pendingAction;
                const ok = await handleUpdate();
                setUnsavedPrompt({ open: false, pendingAction: null });
                if (ok && typeof fn === 'function') fn();
              }}
              data-testid="unsaved-save"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" />
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generate dialog — collects user guidance before calling the LLM */}
      <Dialog open={showGenerateDialog} onOpenChange={(open) => { if (!generating) setShowGenerateDialog(open); }}>
        <DialogContent className="max-w-xl max-h-[85vh] flex flex-col p-0" data-testid="policy-ai-generate-dialog">
          <DialogHeader className="px-5 pt-4 pb-3 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Sparkles className="w-4 h-4 text-violet-600" />
              Generate policy with AI
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Drafting <span className="font-medium text-slate-700">{getPolicyName(policy)}</span>
              {policy?.frameworks?.length ? ` · ${policy.frameworks.join(', ')}` : ''}. Fields are optional except Company.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={runAiGenerate} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            <div>
              <Label htmlFor="ai-company-name" className="text-xs">
                Company Name <span className="text-red-500" aria-hidden>*</span>
              </Label>
              <Input
                id="ai-company-name"
                value={generateForm.company_name}
                onChange={(e) => setGenerateForm(f => ({ ...f, company_name: e.target.value }))}
                placeholder="e.g. Acme, Inc."
                maxLength={120}
                className="h-9 text-sm"
                data-testid="ai-generate-company-input"
              />
            </div>

            <div>
              <Label htmlFor="ai-audience" className="text-xs">Intended audience</Label>
              <Input
                id="ai-audience"
                value={generateForm.audience}
                onChange={(e) => setGenerateForm(f => ({ ...f, audience: e.target.value }))}
                placeholder="All employees · Engineering · Contractors"
                className="h-9 text-sm"
                data-testid="ai-generate-audience-input"
              />
            </div>

            <div>
              <Label htmlFor="ai-must-include" className="text-xs">Must include</Label>
              <Textarea
                id="ai-must-include"
                value={generateForm.must_include}
                onChange={(e) => setGenerateForm(f => ({ ...f, must_include: e.target.value }))}
                placeholder="BYOD requirements, MFA on admins, examples of acceptable use…"
                className="min-h-[56px] text-xs"
                data-testid="ai-generate-must-include-input"
              />
            </div>

            <div>
              <Label htmlFor="ai-must-avoid" className="text-xs">Avoid</Label>
              <Textarea
                id="ai-must-avoid"
                value={generateForm.must_avoid}
                onChange={(e) => setGenerateForm(f => ({ ...f, must_avoid: e.target.value }))}
                placeholder="Skip physical-access controls, no vendor names…"
                className="min-h-[44px] text-xs"
                data-testid="ai-generate-must-avoid-input"
              />
            </div>

            <div>
              <Label htmlFor="ai-context" className="text-xs">Additional context</Label>
              <Textarea
                id="ai-context"
                value={generateForm.additional_context}
                onChange={(e) => setGenerateForm(f => ({ ...f, additional_context: e.target.value }))}
                placeholder="Remote-first SaaS, ~50 ppl, EU+US"
                className="min-h-[44px] text-xs"
                data-testid="ai-generate-context-input"
              />
            </div>
          </form>

          <DialogFooter className="px-5 py-3 border-t border-slate-100">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowGenerateDialog(false)} disabled={generating}
                    data-testid="ai-generate-cancel-btn">
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={runAiGenerate} disabled={generating || !(generateForm.company_name || '').trim()}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="ai-generate-submit-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              {generating ? 'Generating…' : 'Generate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Publish Policy</DialogTitle>
            <DialogDescription>
              Are you sure you want to publish this policy? All employees will be notified and required to accept it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handlePublish} className="bg-navy-600 hover:bg-navy-700">
              Publish Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unpublish Confirmation Dialog */}
      <Dialog open={showUnpublishDialog} onOpenChange={(open) => {
        setShowUnpublishDialog(open);
        if (!open) setConfirmationText('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertCircle className="w-5 h-5" />
              Confirm Unpublish Policy
            </DialogTitle>
            <DialogDescription>
              This will remove the policy from employees' pending acceptance lists. Existing acceptances will be preserved.
              <br /><br />
              Type <strong>UNPUBLISH</strong> to confirm:
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Type UNPUBLISH"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
            className="mt-2"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowUnpublishDialog(false);
              setConfirmationText('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleUnpublish}
              disabled={confirmationText !== 'UNPUBLISH'}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Unpublish Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Policy</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive this policy? It will no longer be visible or required for employees.
              You can unarchive it later if needed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleArchive} className="bg-slate-600 hover:bg-slate-700">
              Archive Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unarchive Confirmation Dialog */}
      <Dialog open={showUnarchiveDialog} onOpenChange={setShowUnarchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unarchive Policy</DialogTitle>
            <DialogDescription>
              This will restore the policy to draft status. You can then edit and publish it again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnarchiveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUnarchive} className="bg-navy-600 hover:bg-navy-700">
              Unarchive Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versioning: PDF upload / AI regenerate. New versions are
          auto-activated on success — per user spec, an admin who
          uploads/regenerates a new version wants it to become the
          live version immediately without a separate "Set as active"
          step. Failure to activate is non-fatal: the version still
          exists in history and admins can retry from the version
          selector. */}
      <UploadPdfVersionDialog
        open={showUploadPdfDialog}
        onOpenChange={setShowUploadPdfDialog}
        policyId={policyId}
        onSuccess={async (newVersionNumber) => {
          if (newVersionNumber) {
            try {
              await axios.post(
                `/api/policies/${policyId}/versions/${newVersionNumber}/activate`,
                {},
                getAuthHeaders(),
              );
            } catch (_e) { /* best effort — version is preserved regardless */ }
          }
          await fetchPolicy();
          if (newVersionNumber) await handleSelectPreviewVersion(newVersionNumber);
        }}
      />
      <AIRegenerateVersionDialog
        open={showAIRegenDialog}
        onOpenChange={setShowAIRegenDialog}
        policyId={policyId}
        sourceVersionNumber={previewVersionNumber}
        sourceVersionSummary={previewVersion}
        onSuccess={async (newVersionNumber) => {
          if (newVersionNumber) {
            try {
              await axios.post(
                `/api/policies/${policyId}/versions/${newVersionNumber}/activate`,
                {},
                getAuthHeaders(),
              );
            } catch (_e) { /* best effort */ }
          }
          await fetchPolicy();
          if (newVersionNumber) await handleSelectPreviewVersion(newVersionNumber);
        }}
      />
      {/* "Set as active version" confirmation modal removed — new
          versions are now activated automatically on upload/regenerate. */}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => {
        setShowDeleteDialog(open);
        if (!open) setConfirmationText('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Permanently Delete Policy
            </DialogTitle>
            <DialogDescription>
              <strong className="text-red-600">⚠️ This action cannot be undone!</strong>
              <br /><br />
              This will permanently delete:
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>The policy and all its content</li>
                <li>All version history</li>
                <li>All employee acceptance records</li>
              </ul>
              <br />
              Type <strong>DELETE</strong> to confirm:
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Type DELETE"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            className="mt-2"
            data-testid="policy-delete-confirm-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteDialog(false);
              setConfirmationText('');
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={confirmationText !== 'DELETE'}
              className="bg-red-600 hover:bg-red-700"
              data-testid="policy-delete-confirm-btn"
            >
              Permanently Delete Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --------- Uploaded-PDF preview dialog (change b) --------- */}
      <Dialog open={!!pdfPreview} onOpenChange={(open) => { if (!open) closePdfPreview(); }}>
        <DialogContent
          className="max-w-[90vw] w-[90vw] h-[90vh] p-0 gap-0 flex flex-col"
          data-testid="policy-pdf-preview-dialog"
        >
          <DialogHeader className="px-6 py-4 border-b border-slate-200 shrink-0">
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <span className="truncate">{policy?.name || 'Policy'}</span>
              <span className="text-slate-400">·</span>
              <span className="text-sm text-slate-600 truncate">{pdfPreview?.filename}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-100 relative overflow-hidden">
            {pdfPreview?.loading && (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500" data-testid="policy-pdf-preview-loading">
                Loading document…
              </div>
            )}
            {pdfPreview?.error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-sm text-slate-600 px-6 text-center" data-testid="policy-pdf-preview-error">
                <AlertCircle className="w-8 h-8 text-amber-500" />
                <p>{pdfPreview.error} Your browser may not support inline PDF preview.</p>
                <Button
                  onClick={() => handleAttachmentDownload(pdfPreview.fileId, pdfPreview.filename)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Download className="w-4 h-4 mr-2" /> Download to view
                </Button>
              </div>
            )}
            {pdfPreview?.url && !pdfPreview.error && (
              /* Chrome blocks blob: URLs served through <iframe> in
                 many CSP configurations ("This content is blocked").
                 Using <object type="application/pdf"> routes through
                 the native PDF viewer instead — reliably renders in
                 Chrome, Edge, and Safari. If the browser can't render
                 inline, the child fallback content shows an "Open in
                 new tab" affordance and a Download button. */
              <object
                data={pdfPreview.url}
                type="application/pdf"
                className="w-full h-full"
                data-testid="policy-pdf-preview-frame"
                aria-label={`${pdfPreview.filename} preview`}
              >
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-sm text-slate-600 px-6 text-center">
                  <AlertCircle className="w-8 h-8 text-amber-500" />
                  <p>Inline PDF preview is not supported in this browser.</p>
                  <div className="flex gap-2">
                    <a
                      href={pdfPreview.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm font-medium"
                      data-testid="policy-pdf-preview-new-tab"
                    >
                      Open in new tab
                    </a>
                    <Button
                      onClick={() => handleAttachmentDownload(pdfPreview.fileId, pdfPreview.filename)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Download className="w-4 h-4 mr-2" /> Download
                    </Button>
                  </div>
                </div>
              </object>
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-slate-200 shrink-0">
            {pdfPreview?.url && (
              <a
                href={pdfPreview.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm font-medium"
                data-testid="policy-pdf-preview-newtab-btn"
              >
                Open in new tab
              </a>
            )}
            {pdfPreview && (
              <Button
                variant="outline"
                onClick={() => handleAttachmentDownload(pdfPreview.fileId, pdfPreview.filename)}
                data-testid="policy-pdf-preview-download-btn"
              >
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
            )}
            <Button
              onClick={closePdfPreview}
              data-testid="policy-pdf-preview-close-btn"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --------- Uploaded-PDF remove confirmation (change c) --------- */}
      <Dialog
        open={!!pendingRemoveFile}
        onOpenChange={(open) => {
          if (!open && !pendingRemoveFile?.submitting) setPendingRemoveFile(null);
        }}
      >
        <DialogContent data-testid="policy-remove-file-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Remove uploaded PDF?
            </DialogTitle>
            <DialogDescription>
              This permanently removes <strong>{pendingRemoveFile?.filename}</strong> from this policy. This action cannot be undone.
              <br /><br />
              Type <strong>DELETE</strong> to confirm:
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Type DELETE"
            value={pendingRemoveFile?.typed || ''}
            onChange={(e) => setPendingRemoveFile((prev) => (prev ? { ...prev, typed: e.target.value } : prev))}
            disabled={pendingRemoveFile?.submitting}
            className="mt-2"
            data-testid="policy-remove-file-confirm-input"
          />
          {pendingRemoveFile?.error && (
            <p className="text-xs text-red-600 mt-2" data-testid="policy-remove-file-error">
              {pendingRemoveFile.error}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingRemoveFile(null)}
              disabled={pendingRemoveFile?.submitting}
              autoFocus
              data-testid="policy-remove-file-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmAttachmentRemove}
              disabled={pendingRemoveFile?.typed !== 'DELETE' || pendingRemoveFile?.submitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="policy-remove-file-confirm-btn"
            >
              {pendingRemoveFile?.submitting ? 'Removing…' : 'Permanently Remove PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Acceptances Detail Dialog */}
      <Dialog open={showAcceptancesDialog} onOpenChange={setShowAcceptancesDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Policy Acceptance Report</DialogTitle>
            <DialogDescription>
              Detailed acceptance tracking for "{getPolicyName(policy)}"
            </DialogDescription>
          </DialogHeader>
          {acceptances && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{acceptances.total_employees}</p>
                  <p className="text-sm text-slate-600">Total Employees</p>
                </div>
                <div className="text-center p-4 bg-emerald-50 rounded-lg">
                  <p className="text-2xl font-bold text-navy-600">{acceptances.accepted_count}</p>
                  <p className="text-sm text-slate-600">Accepted</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <p className="text-2xl font-bold text-orange-600">{acceptances.pending_count}</p>
                  <p className="text-sm text-slate-600">Pending</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div>
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div 
                    className="bg-navy-600 h-3 rounded-full transition-all"
                    style={{width: `${acceptances.acceptance_rate}%`}}
                  ></div>
                </div>
                <p className="text-sm text-slate-600 mt-1 text-center">
                  {acceptances.acceptance_rate.toFixed(1)}% acceptance rate
                </p>
              </div>

              {/* Accepted Employees */}
              {acceptances.acceptances && acceptances.acceptances.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-navy-600" />
                    Accepted ({acceptances.accepted_count})
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {acceptances.acceptances.map((acceptance) => (
                      <div key={acceptance.acceptance_id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div>
                          <p className="font-medium text-slate-900">{acceptance.user_name}</p>
                          <p className="text-xs text-slate-600">{acceptance.user_email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-navy-700">Version {acceptance.version_accepted}</p>
                          <p className="text-xs text-slate-500">
                            {formatDate(acceptance.accepted_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending Employees */}
              {acceptances.pending && acceptances.pending.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-orange-600" />
                    Pending Acceptance ({acceptances.pending_count})
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {acceptances.pending.map((user) => (
                      <div key={user.user_id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <div>
                          <p className="font-medium text-slate-900">{user.user_name}</p>
                          <p className="text-xs text-slate-600">{user.user_email}</p>
                        </div>
                        <Badge variant="outline" className="text-orange-700 border-orange-300">
                          {user.user_role}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowAcceptancesDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

PolicyDetail.displayName = 'PolicyDetail';

// -----------------------------------------------------------------------
// Inline attached-file row.
// Renders inside the Policy Content card for pdf_upload versions —
// replaces the previous standalone "Attachments" section so the file
// is presented as the version's content itself, not a side artefact.
// -----------------------------------------------------------------------
const AttachedFileRow = ({ file, onOpen, onDownload, onRemove, removing }) => {
  const size = file.size || 0;
  const sizeStr =
    size < 1024
      ? `${size} B`
      : size < 1024 * 1024
      ? `${(size / 1024).toFixed(1)} KB`
      : `${(size / 1024 / 1024).toFixed(1)} MB`;
  return (
    <li
      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5"
      data-testid={`policy-content-file-${file.link_id}`}
    >
      <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
        <FileText className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-800 truncate" title={file.filename}>
            {file.filename}
          </span>
          {file.source === 'bulk_attach' && (
            <Badge
              variant="outline"
              className="border-blue-200 text-blue-700 bg-blue-50 text-[10px] uppercase tracking-wide"
              data-testid={`policy-content-file-bulk-badge-${file.link_id}`}
            >
              Bulk attached
            </Badge>
          )}
        </div>
        <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span>{sizeStr}</span>
          <span>·</span>
          <span>{file.uploaded_at ? formatDateTime(file.uploaded_at) : ''}</span>
          {file.uploaded_by_name && (
            <>
              <span>·</span>
              <span>by {file.uploaded_by_name}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          size="sm"
          variant="ghost"
          onClick={onOpen}
          data-testid={`policy-content-file-open-${file.link_id}`}
          aria-label={`Preview ${file.filename}`}
        >
          Preview
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDownload}
          data-testid={`policy-content-file-download-${file.link_id}`}
          aria-label={`Download ${file.filename}`}
        >
          Download
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRemove}
          disabled={removing}
          className="text-red-600 hover:bg-red-50"
          data-testid={`policy-content-file-remove-${file.link_id}`}
          aria-label={`Remove ${file.filename}`}
        >
          {removing ? 'Removing…' : 'Remove'}
        </Button>
      </div>
    </li>
  );
};

export default PolicyDetail;
